import React, { forwardRef, useImperativeHandle, useEffect, useState } from "react";
import { useFormik } from "formik";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  Alert,
  Button
} from "@mui/material";
import { useRecoilValue } from "recoil";
import { authState } from "../../Recoil/RecoilState";
import axios from "axios";
import { baseURL } from "../../utils/StaticVariables";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import DatePickerInput from "../Inputs/DatePicker";

dayjs.extend(customParseFormat);

const makeInitial = (filter) => ({
  start_date: filter?.start_date ? dayjs(filter.start_date, "YYYY-MM-DD") : null,
  end_date: filter?.end_date ? dayjs(filter.end_date, "YYYY-MM-DD") : null,
  buildings: Array.isArray(filter?.buildings) ? filter.buildings : filter?.buildings ? filter.buildings.split(",").filter(Boolean) : [],
  floors: Array.isArray(filter?.floors) ? filter.floors : filter?.floors ? filter.floors.split(",").filter(Boolean) : [],
  zones: Array.isArray(filter?.zones) ? filter.zones : filter?.zones ? filter.zones.split(",").filter(Boolean) : [],
  areas: Array.isArray(filter?.areas) ? filter.areas : filter?.areas ? filter.areas.split(",").filter(Boolean) : [],
  locations: Array.isArray(filter?.locations) ? filter.locations : filter?.locations ? filter.locations.split(",").filter(Boolean) : [],
});

const VideosFilter = forwardRef(({ filter, changeFilterHandle }, ref) => {
  const { token } = useRecoilValue(authState);

  const [locations, setLocations] = useState([]);
  const [areas, setAreas] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hierarchyError, setHierarchyError] = useState(null);

  const formik = useFormik({
    initialValues: makeInitial(filter),
    enableReinitialize: true,
    onSubmit: (values) => {
      changeFilterHandle({
        start_date: values.start_date?.format("YYYY-MM-DD") ?? null,
        end_date: values.end_date?.format("YYYY-MM-DD") ?? null,
        buildings: values.buildings.join(","),
        floors: values.floors.join(","),
        zones: values.zones.join(","),
        areas: values.areas.join(","),
        locations: values.locations.join(","),
      });
    },
  });

  const clearAllFilters = () => {
    formik.resetForm({
      values: {
        locations: [],
        areas: [],
        buildings: [],
        floors: [],
        zones: [],
        start_date: null,
        end_date: null,
      },
    });

    setAreas([]);
    setBuildings([]);
    setFloors([]);
    setZones([]);
    setHierarchyError(null);
    formik.setTouched({});
  };

  // 🟢 Fetch Locations
  useEffect(() => {
    const fetchLocations = async () => {
      setLoading(true);
      setHierarchyError(null);
      try {
        const { data } = await axios.get(`${baseURL}locations/list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLocations(data.locations || []);
      } catch (error) {
        setHierarchyError("Failed to load locations");
        console.error("Error fetching locations:", error);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchLocations();
    }
  }, [token]);

  // 🟢 Fetch Areas when locations change
  useEffect(() => {
    if (formik.values.locations.length > 0) {
      const fetchAreas = async () => {
        setHierarchyError(null);
        try {
          const { data } = await axios.get(
            `${baseURL}areas/list?locations=${formik.values.locations.join(",")}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setAreas(data.areas || []);
        } catch (error) {
          setHierarchyError("Failed to load areas");
          console.error("Error fetching areas:", error);
        }
      };
      fetchAreas();
    } else {
      setAreas([]);
      // Clear dependent selections
      formik.setFieldValue("areas", []);
      setBuildings([]);
      formik.setFieldValue("buildings", []);
      setFloors([]);
      formik.setFieldValue("floors", []);
      setZones([]);
      formik.setFieldValue("zones", []);
    }
  }, [formik.values.locations, token]);

  // 🟢 Fetch Buildings when areas change
  useEffect(() => {
    if (formik.values.areas.length > 0) {
      const fetchBuildings = async () => {
        setHierarchyError(null);
        try {
          const { data } = await axios.get(
            `${baseURL}buildings/list?areas=${formik.values.areas.join(",")}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setBuildings(data.buildings || []);
        } catch (error) {
          setHierarchyError("Failed to load buildings");
          console.error("Error fetching buildings:", error);
        }
      };
      fetchBuildings();
    } else {
      setBuildings([]);
      // Clear dependent selections
      formik.setFieldValue("buildings", []);
      setFloors([]);
      formik.setFieldValue("floors", []);
      setZones([]);
      formik.setFieldValue("zones", []);
    }
  }, [formik.values.areas, token]);

  // 🟢 Fetch Floors when buildings change
  useEffect(() => {
    if (formik.values.buildings.length > 0) {
      const fetchFloors = async () => {
        setHierarchyError(null);
        try {
          const { data } = await axios.get(
            `${baseURL}floor-levels/list?buildings=${formik.values.buildings.join(",")}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setFloors(data.floor_levels || []);
        } catch (error) {
          setHierarchyError("Failed to load floors");
          console.error("Error fetching floors:", error);
        }
      };
      fetchFloors();
    } else {
      setFloors([]);
      // Clear dependent selections
      formik.setFieldValue("floors", []);
      setZones([]);
      formik.setFieldValue("zones", []);
     
    }
  }, [formik.values.buildings, token]);

  // 🟢 Fetch Zones when floors change
  useEffect(() => {
    if (formik.values.floors.length > 0) {
      const fetchZones = async () => {
        setHierarchyError(null);
        try {
          const { data } = await axios.get(
            `${baseURL}zones/list?floor_levels=${formik.values.floors.join(",")}`, // Fixed: was floor-levels_id
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setZones(data.zones || []);
        } catch (error) {
          setHierarchyError("Failed to load zones");
          console.error("Error fetching zones:", error);
        }
      };
      fetchZones();
    } else {
      setZones([]);
      // Clear dependent selections
      formik.setFieldValue("zones", []);
   
    }
  }, [formik.values.floors, token]);

  // 🟢 Fetch Cameras when zones change
  useEffect(() => {
    if (formik.values.zones.length > 0) {
      const fetchCameras = async () => {
        setHierarchyError(null);
        try {
          await axios.get(
            `${baseURL}streams/locations/search?location_type=zones&q=${formik.values.zones.join(",")}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (error) {
          setHierarchyError("Failed to load cameras");
          console.error("Error fetching cameras:", error);
        }
      };
      fetchCameras();
    }
  }, [formik.values.zones, token]);

  useImperativeHandle(ref, () => ({
    submit: formik.handleSubmit,
  }));

  const handleCheckboxChange = (field, value) => {
    const selected = Array.isArray(formik.values[field]) ? formik.values[field] : [];
    const updated = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    formik.setFieldValue(field, updated);
  };

  const handleSelectAll = (field, options, valueKey) => {
    const allValues = options.map(opt => opt[valueKey]);
    const currentValues = formik.values[field] || [];
    const allSelected = allValues.every(val => currentValues.includes(val));
    
    formik.setFieldValue(field, allSelected ? [] : allValues);
  };

  const renderCheckboxGroup = (label, field, options, valueKey = "location", isLoading = false, displayKey = null) => {
    const selectedCount = formik.values[field]?.length || 0;
    const totalCount = options.length;
    const actualDisplayKey = displayKey || valueKey;
    
    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography fontWeight={600}>
            {label} {selectedCount > 0 && `(${selectedCount} selected)`}
          </Typography>
          {options.length > 0 && (
            <Button
              size="small"
              onClick={() => handleSelectAll(field, options, valueKey)}
            >
              {selectedCount === totalCount ? 'Deselect All' : 'Select All'}
            </Button>
          )}
        </Stack>
        
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        ) : options.length === 0 ? (
          <Typography variant="body2" color="text.secondary" py={1}>
            No {label.toLowerCase()} available
          </Typography>
        ) : (
          <Box sx={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 1,
            maxHeight: 200,
            overflowY: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 1
          }}>
            {options.map((opt) => (
              <FormControlLabel
                key={opt.id}
                control={
                  <Checkbox
                    size="small"
                    checked={formik.values[field]?.includes(opt[valueKey]) || false}
                    onChange={() => handleCheckboxChange(field, opt[valueKey])}
                  />
                }
                label={opt[actualDisplayKey]}
                sx={{ margin: 0 }}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography fontWeight={600}>Location Filters</Typography>
        <Button size="small" onClick={clearAllFilters} color="secondary">
          Clear All Filters
        </Button>
      </Stack>
      
      <Stack gap={3} component="form" onSubmit={formik.handleSubmit}>
        {loading && <CircularProgress size={24} />}
        {hierarchyError && <Alert severity="error">{hierarchyError}</Alert>}
        
        {!loading && !hierarchyError && (
          <>
            {renderCheckboxGroup("Locations", "locations", locations)}
            {formik.values.locations.length > 0 && renderCheckboxGroup("Areas", "areas", areas, 'area')}
            {formik.values.areas.length > 0 && renderCheckboxGroup("Buildings", "buildings", buildings, 'building')}
            {formik.values.buildings.length > 0 && renderCheckboxGroup("Floors", "floors", floors, 'floor_level')}
            {formik.values.floors.length > 0 && renderCheckboxGroup("Zones", "zones", zones, "zone")}
          </>
        )}


        {/* Date and Time Pickers */}
           <DatePickerInput
                   label="Start Date"
                   dateValue={formik.values.start_date}
                   onDateChange={(d) => formik.setFieldValue("start_date", d)}
                   maxDate={formik.values.end_date}
                   error={Boolean(formik.errors.start_date)}
                   helperText={formik.errors.start_date}
                 />
                 
                 <DatePickerInput
                   label="End Date"
                   dateValue={formik.values.end_date}
                   onDateChange={(d) => formik.setFieldValue("end_date", d)}
                   minDate={formik.values.start_date}
                   error={Boolean(formik.errors.end_date)}
                   helperText={formik.errors.end_date}
                 />
      </Stack>
    </LocalizationProvider>
  );
});

VideosFilter.displayName = 'VideosFilter';

export default VideosFilter;